import * as Columns from './Columns'

/**
* Creates a column component based on the columnType specified in the column definition.
* Used by LazyGrid for server-side data loading scenarios.
*
 * Available column types:
 * - 'text': Standard text column with optional filtering/sorting
 * - 'numeric': Numeric column with equals filter
 * - 'date': Date column with date picker filter
 * - 'year': Year column with numeric filter
 * - 'boolean': Boolean column with tri-state checkbox filter
 * - 'dropdown': Dropdown column with dropdown filter
 * - 'contacttype': Contact type column with automatic contact type dropdown filter
 * - 'membershiptype': Membership type column with automatic membership type dropdown filter
 * - 'companytype': Company type column with automatic company type dropdown filter
 * - 'idaCode': IDA code column with special formatting
 * - 'expander': Row expander column for nested data
 * - 'actions': Action buttons column
*
* @param {Object} params - Column configuration
* @param {string} params.columnType - Type of column to create (required)
* @param {string} params.title - Column header title
* @param {string} params.mapsToField - Field name in data
* @param {boolean} params.filter - Enable filtering
* @param {boolean} params.sortable - Enable sorting
* @param {boolean} params.editable - Enable editing
* @param {Function} params.template - Custom display template
* @param {Function|boolean} params.expand - Row expansion config (for expander type)
* @param {Function} params.isFieldChanged - Function to check if field changed
* @param {Object} params.validationErrors - Validation errors object
* @param {string} params.dataKey - Primary key field name
* @param {Array} params.channels - Available channels
* @param {Function} params.isEditable - Function to check if row is editable
 * @param {Array} params.dropdownOptions - Options for dropdown filter (for dropdown type)
 * @param {string} params.filterPlaceholder - Placeholder for dropdown filter (for dropdown type)
 * @param {Function} params.filterTemplate - Custom filter template (for dropdown type)
 * @returns {JSX.Element} Column component
 */
export const createColumn = ({
    columnType,
    title,
    mapsToField,    
    filter,
    expand,
    template,
    sortable,
    editable,
    isFieldChanged,
    isFieldChangedOverride,
    validationErrors,
    dataKey,
    channels,
    isEditable,
    originalData,
    style,
    filterElement,
    filterMatchMode
}) => {
    const columnProps = {
        title,
        mapsToField,      
        filter,
        sortable,
        editable,
        isFieldChanged,
        isFieldChangedOverride,
        validationErrors,
        template,
        isEditable,
        originalData,
        style,
        filterElement,
        filterMatchMode
    }

    // Map column types to column components
    const typeMap = {
        'text': Columns.TextColumn,
        'numeric': Columns.NumericColumn,
        'date': Columns.DateColumn,
        'year': Columns.YearColumn,
        'boolean': Columns.BooleanColumn,               
        'contacttype': Columns.ContactTypeColumn,
        'membershiptype': Columns.MembershipTypeColumn,
        'companytype': Columns.CompanyTypeColumn,
        'programmetype': Columns.ProgrammeTypeColumn,
        'worktype': Columns.WorkTypeColumn,
        'workcontributiontype': Columns.WorkContributionTypeColumn,
        'avtype': Columns.AvTypeColumn,
        'idaCode': Columns.IdaCodeColumn,
        'expander': Columns.ExpanderColumn,
        'actions': Columns.ActionsColumn
    }

    // Handle expander column
    if (columnType === 'expander') {
        return Columns.ExpanderColumn({ title, expand })
    }

    // Handle actions column
    if (columnType === 'actions') {
        return Columns.ActionsColumn({ template })
    }

    // Get the appropriate column component or default to TextColumn
    const ColumnComponent = typeMap[columnType] || Columns.TextColumn
    return ColumnComponent(columnProps)
}
